import React, { useEffect, useRef, useState } from "react"
import {
  Row,
  Col,
  Form,
  Label,
  FormGroup,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  UncontrolledTooltip,
  Collapse,
} from "reactstrap"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import { Switch, Table, Select, Divider, Input as AntdInput, Form as AntdForm } from "antd"
import {
  booleanSorter,
  downloadExcel,
  showToast,
  stringSorter,
  boolFormatterX,
} from "utils/general"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  deleteNewsletterSubscribers,
  getNewsletterSubscribers,
  insertNewsletterSub,
  updateNewsletterSub,
} from "helpers/database_helper"

const { Option } = Select

const RecipientLists = props => {
  const [subs, setSubs] = useState([])
  const [filteredSubs, setFilteredSubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [modal, setModal] = useState(false)
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [filterVisible, setFilterVisible] = useState(false)
  const [searchName, setSearchName] = useState("")
  const [searchEmail, setSearchEmail] = useState("")
  const [searchTags, setSearchTags] = useState([])
  const [searchGroupname, setSearchGroupname] = useState([])
  const [searchType, setSearchType] = useState("0")
  const [subValues, setSubValues] = useState({
    name: "",
    email: "",
    tags: "",
    groupname: "",
    release_notes: false,
    status_reports: false,
  })

  const abortRef = useRef(null)

  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    setSubs(props.subscribers)
    setLoading(false)
  }, [props.subscribers])

  useEffect(() => {
    setFilteredSubs(subs)
  }, [subs])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchName, searchEmail, searchGroupname, searchType])

  // Handle search function
  const handleSearch = () => {
    let searchNameLowercase = searchName.toLowerCase()
    let searchEmailLowercase = searchEmail.toLowerCase()
    let result = subs.filter(
      data =>
        (searchTags.length === 0 ||
          searchTags.some(tag => data.tags?.includes(tag))) &&
        (searchGroupname.length === 0 ||
          searchGroupname.some(group => data.groupname?.includes(group))) &&
        data.name.toLowerCase().includes(searchNameLowercase) &&
        data.email.toLowerCase().includes(searchEmailLowercase) &&
        (searchType == 0 ||
          (searchType === "1" && data.release_notes === 1) ||
          (searchType === "2" && data.status_reports === 1))
    )
    setFilteredSubs(result)
  }

  const settingNewSelection = () => {
    let newEntry = {
      name: "",
      email: "",
      tags: "",
      groupname: "",
      release_notes: 0,
      status_reports: 0,
    }
    setSelected(null)
    setSubValues(newEntry)
    toggleModal()
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: subValues.name,
      email: subValues.email,
      tags: subValues.tags,
      groupname: subValues.groupname,
      release_notes: subValues.release_notes,
      status_reports: subValues.status_reports,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(props.t("Required field")),
      email: Yup.string().required(props.t("Required field")),
    }),
    onSubmit: values => {
      if (selected) {
        let formattedValuesUpdate = {
          id: selected.id,
          name: values.name,
          tags: values.tags,
          groupname: values.groupname,
          release_notes: values.release_notes ? 1 : 0,
          status_reports: values.status_reports ? 1 : 0,
        }
        // console.log("Submit values: ", selected.id, formattedValuesUpdate)
        updateSub(formattedValuesUpdate)
      } else {
        let formattedValues = {
          ...values,
          release_notes: values.release_notes ? 1 : 0,
          status_reports: values.status_reports ? 1 : 0,
        }
        // console.log("Submit values: ", formattedValues)
        insertSub(formattedValues)
      }
    },
  })

  const fetchSubs = () => {
    setLoading(true)
    getNewsletterSubscribers(abortRef.current, false, {})
      .then(subs => {
        // console.log("Subscribers: ", subs)
        setSubs(subs)
        props.updatingSubs(subs)
      })
      .catch(err => {
        console.log(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const toggleModal = () => {
    setModal(!modal)
  }

  const toggleConfirmDeleteModal = () => {
    setConfirmDeleteModal(!confirmDeleteModal)
  }

  const insertSub = values => {
    insertNewsletterSub(abortRef.current, values, false)
      .then(result => {
        if (result === "OK") {
          showToast(props.t("Updated"), "success")
          fetchSubs()
        } else {
          showToast(props.t("Error"), "error")
        }
      })
      .catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error")
      })
      .finally(() => {
        setLoading(false)
        setModal(false)
        settingNewSelection()
      })
  }

  const updateSub = values => {
    updateNewsletterSub(abortRef.current, values, false)
      .then(result => {
        if (result === "OK") {
          showToast(props.t("Updated"), "success")
          fetchSubs()
        } else {
          showToast(props.t("Error"), "error")
        }
      })
      .catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error")
      })
      .finally(() => {
        setLoading(false)
        setModal(false)
        settingNewSelection()
      })
  }

  const confirmDeleteSubs = () => {
    setConfirmDeleteModal(true)
  }

  const deleteSubs = () => {
    if (selectedRowKeys.length === 0) {
      showToast(props.t("No recipients selected"), "warning")
      return
    }

    setLoading(true)
    deleteNewsletterSubscribers(abortRef.current, selectedRowKeys.join(), false)
      .then(result => {
        if (result === "OK") {
          showToast(props.t("Deleted"), "success")
          fetchSubs()
        } else {
          console.log("Delete: ", result)
          showToast(props.t("Error"), "error")
        }
      })
      .catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error")
      })
      .finally(() => {
        setLoading(false)
        setSelectedRowKeys([])
        setConfirmDeleteModal(false)
      })
  }

  const handleTableChange = selectedRowKeys => {
    setSelectedRowKeys(selectedRowKeys)
  }

  const handleTagsChange = value => {
    const filteredValues = value.filter(val => val.trim() !== "")
    const tagsString = filteredValues.join(",")
    validation.setFieldValue("tags", tagsString)
  }

  const handleGroupnameChange = value => {
    const filteredValues = value.filter(val => val.trim() !== "")
    const groupnameString = filteredValues.join(",")
    validation.setFieldValue("groupname", groupnameString)
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [
        props.t("Name"),
        props.t("Email"),
        props.t("Tags"),
        props.t("Group name"),
        props.t("Release notes"),
        props.t("Error bulletins"),
      ],
    ]
    const data = filteredSubs.map(elt => [
      elt.name,
      elt.email,
      elt.tags,
      elt.groupname,
      boolFormatterX(elt.release_notes),
      boolFormatterX(elt.status_reports),
    ])
    downloadExcel(props.t("Mailing list"), heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  // Separate tags
  const allTags = [
    ...new Set(
      subs
        .flatMap(sub =>
          sub.tags ? sub.tags.split(",").map(tag => tag.trim()) : []
        )
        .filter(tag => tag)
    ),
  ]

  // Separate group names
  const allGroupnames = [
    ...new Set(
      subs
        .flatMap(sub =>
          sub.groupname
            ? sub.groupname.split(",").map(group => group.trim())
            : []
        )
        .filter(group => group)
    ),
  ]

  const columns = [
    {
      title: props.t("Name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      ellipsis: true,
    },
    {
      title: props.t("Email"),
      dataIndex: "email",
      key: "email",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "email", sortOrder)
      },
      ellipsis: true,
    },
    {
      title: props.t("Tags"),
      dataIndex: "tags",
      key: "tags",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "tags", sortOrder)
      },
    },
    {
      title: props.t("Group name"),
      dataIndex: "groupname",
      key: "groupname",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "groupname", sortOrder)
      },
      ellipsis: true,
    },
    {
      title: props.t("Release notes"),
      dataIndex: "release_notes",
      key: "release_notes",
      render: cell => {
        return cell ? (
          <i className="mdi mdi-check-bold text-primary" />
        ) : (
          <></>
        )
      },
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "release_notes", sortOrder)
      },
      ellipsis: true,
    },
    {
      title: props.t("Error bulletins"),
      dataIndex: "status_reports",
      key: "status_reports",
      render: cell => {
        return cell ? (
          <i className="mdi mdi-check-bold text-primary" />
        ) : (
          <></>
        )
      },
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "status_reports", sortOrder)
      },
      ellipsis: true,
    },
    {
      title: props.t("Actions"),
      key: "actions",
      render: (text, record) => (
        <Button
          color="primary"
          size="sm"
          onClick={() => {
            setSelected(record)
            setSubValues(record)
            toggleModal()
          }}
        >
          {props.t("Edit")}
        </Button>
      ),
      ellipsis: true,
    },
  ]

  return (
    <>
      <Row className="mb-3">
        <Col>
          <Button
            className="compact-button me-3"
            id="filter-button"
            color="primary"
            outline
            onClick={() => setFilterVisible(!filterVisible)}
          >
            <UncontrolledTooltip
              placement="bottom"
              target="filter-button"
            >
              {props.t("Filters")}
            </UncontrolledTooltip>
            {filterVisible ? (
              <i className="mdi mdi-filter-minus"></i>
            ) : (
              <i className="mdi mdi-filter-menu"></i>
            )}
          </Button>
          <Button
            color="primary"
            className="compact-button me-3"
            onClick={fetchSubs}
          >
            <i className="mdi mdi-refresh me-2"></i>
            {props.t("Refresh")}
          </Button>
          <Button
            color="success"
            className="compact-button me-3"
            onClick={settingNewSelection}
          >
            <i className="mdi mdi-account-plus me-2"></i>
            {props.t("New recipient")}
          </Button>
          {selectedRowKeys.length > 0 && (
            <Button
              color="danger"
              className="compact-button"
              onClick={confirmDeleteSubs}
              disabled={selectedRowKeys.length === 0}
            >
              <i className="mdi mdi-delete me-2"></i>
              {props.t("Delete selected")}
            </Button>
          )}
        </Col>
        <Col className="col-auto">
          <Button
            color="primary"
            outline
            className="compact-button"
            onClick={() => generateExcel()}
          >
            <i className="mdi mdi-download me-2"></i>
            {props.t("Download")} .xlsx
          </Button>
        </Col>
      </Row>

      <Collapse isOpen={filterVisible}>
        <Row className="mb-1">
          <Col md="2">
            <FormGroup>
              <Label>{props.t("Name")}</Label>
              <AntdInput
                allowClear
                placeholder={props.t("Name")}
                value={searchName}
                onChange={(v) => { setSearchName(v.target.value) }}
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>
                {props.t("Email")}
              </Label>
              <AntdInput
                allowClear
                placeholder={props.t("Name")}
                value={searchEmail}
                onChange={(v) => { setSearchEmail(v.target.value) }}
              />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>{props.t("Tags")}</Label>
              <Select
                style={{ width: "100%" }}
                value={searchTags}
                onChange={value => setSearchTags(value)}
                mode="multiple"
              >
                {allTags.map(tag => (
                  <Option key={tag} value={tag}>
                    {tag}
                  </Option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>
                {props.t("Group name")}
              </Label>
              <Select
                style={{ width: "100%" }}
                value={searchGroupname}
                onChange={value => setSearchGroupname(value)}
                mode="multiple"
              >
                {allGroupnames.map(name => (
                  <Option key={name} value={name}>
                    {name}
                  </Option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>
                {props.t("Bulletin type")}
              </Label>
              <Select
                style={{ width: "100%" }}
                value={searchType}
                defaultValue="0"
                onChange={value => setSearchType(value)}
              >
                <Option key="0" value="0">
                  {props.t("All")}
                </Option>
                <Option key="1" value="1">
                  {props.t("Release notes")}
                </Option>
                <Option key="2" value="2">
                  {props.t("Error bulletins")}
                </Option>
              </Select>
            </FormGroup>
          </Col>
        </Row>
      </Collapse>

      <Table
        className="mt-1"
        size="small"
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: handleTableChange,
        }}
        columns={columns}
        dataSource={filteredSubs}
        rowKey="id"
        loading={loading}
      />

      <Modal isOpen={modal} size="lg" centered toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {subValues && selected
            ? subValues.name
            : props.t("Add a new recipient")}
        </ModalHeader>
        <ModalBody>
          <Form
            className="form-horizontal"
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            <Row xs={1} sm={2}>
              <Col>
                <FormGroup>
                  <Label>{props.t("Name")}</Label>
                  <AntdForm.Item
                    validateStatus={validation.errors.name ? "error" : ""}
                    hasFeedback
                    help={validation.errors.name}
                  >
                    <AntdInput
                      allowClear
                      placeholder={props.t("Name")}
                      value={validation.values.name}
                      onChange={(v) => { validation.setFieldValue("name", v.target.value) }}
                    />
                  </AntdForm.Item>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label>{props.t("Email")}</Label>
                  <AntdForm.Item
                    validateStatus={validation.errors.email ? "error" : ""}
                    hasFeedback
                    help={validation.errors.email}
                  >
                    <AntdInput
                      allowClear
                      placeholder={props.t("Email")}
                      value={validation.values.email}
                      onChange={(v) => { validation.setFieldValue("email", v.target.value) }}
                    />
                  </AntdForm.Item>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <AntdForm layout="vertical">
                    <AntdForm.Item
                      label={props.t("Tags")}
                      tooltip={props.t("Choose or create a new tag")}
                    >
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        placeholder={props.t("Add tags")}
                        value={
                          validation.values.tags
                            ? validation.values.tags.split(",")
                            : []
                        }
                        onChange={handleTagsChange}
                        tokenSeparators={[","]}
                      >
                        {allTags.map(tag => (
                          <Option key={tag} value={tag}>
                            {tag}
                          </Option>
                        ))}
                      </Select>
                    </AntdForm.Item>
                  </AntdForm>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <AntdForm layout="vertical">
                    <AntdForm.Item
                      label={props.t("Group name")}
                      tooltip={props.t("Choose or create a new group name")}
                    >
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        placeholder={props.t("Add group names")}
                        value={
                          validation.values.groupname
                            ? validation.values.groupname.split(",")
                            : []
                        }
                        onChange={handleGroupnameChange}
                        tokenSeparators={[","]}
                      >
                        {allGroupnames.map(group => (
                          <Option key={group} value={group}>
                            {group}
                          </Option>
                        ))}
                      </Select>
                    </AntdForm.Item>
                  </AntdForm>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Divider
                orientationMargin="5"
                orientation="left"
                className="font-size-13 text-muted mt-1"
              >
                {props.t("Subscriptions")}
              </Divider>
              <Col>
                <FormGroup>
                  <Label>{props.t("Release notes")}</Label>
                  <Switch
                    className="ms-2"
                    name="release_notes"
                    checked={validation.values.release_notes}
                    onChange={v =>
                      validation.setFieldValue("release_notes", v)
                    }
                    checkedChildren={<i className="mdi mdi-check"></i>}
                    unCheckedChildren={<i className="mdi mdi-close"></i>}
                  ></Switch>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label>{props.t("Error bulletins")}</Label>
                  <Switch
                    className="ms-2"
                    name="status_reports"
                    checked={validation.values.status_reports}
                    onChange={v =>
                      validation.setFieldValue("status_reports", v)
                    }
                    checkedChildren={<i className="mdi mdi-check"></i>}
                    unCheckedChildren={<i className="mdi mdi-close"></i>}
                  ></Switch>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" outline onClick={toggleModal}>
            {props.t("Cancel")}
          </Button>
          <Button color="primary" onClick={validation.handleSubmit}>
            {props.t("Save")}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={confirmDeleteModal}
        centered
        toggle={toggleConfirmDeleteModal}
      >
        <ModalHeader toggle={toggleConfirmDeleteModal}>
          {props.t("Confirm deletion")}
        </ModalHeader>
        <ModalBody>
          <p>
            {props.t(
              "Are you sure you want to delete the following recipients"
            )}
            ?
          </p>
          <ul>
            {subs
              .filter(sub => selectedRowKeys.includes(sub.id))
              .map(sub => (
                <li key={sub.id}>{sub.name}</li>
              ))}
          </ul>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            disabled={loading}
            onClick={toggleConfirmDeleteModal}
          >
            {props.t("Cancel")}
          </Button>
          <Button color="danger" disabled={loading} onClick={deleteSubs}>
            {props.t("Delete")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

RecipientLists.propTypes = {
  t: PropTypes.any,
  subscribers: PropTypes.any,
  updatingSubs: PropTypes.any,
}

export default withTranslation()(RecipientLists)
